<template>
  <div class="debug-container">
    <div class="cyber-grid"></div>
    <div class="cyber-glow"></div>
    <div class="particle-container">
      <div class="particle p1"></div>
      <div class="particle p2"></div>
      <div class="particle p3"></div>
      <div class="particle p4"></div>
      <div class="particle p5"></div>
      <div class="particle p6"></div>
      <div class="particle p7"></div>
      <div class="particle p8"></div>
      <div class="particle p9"></div>
      <div class="particle p10"></div>
      <div class="particle p11"></div>
      <div class="particle p12"></div>
    </div>
    
    <div class="debug-header">
      <div class="header-content">
        <div class="title-container">
          <div class="cyber-line"></div>
          <h1 class="debug-title">{{ langMap.networkDebugInfo[lang] }}</h1>
        </div>
        <div class="robot-code">
          <span class="code-label">ID:</span>
          <span class="code-value">{{ robotCode }}</span>
          <div class="pulse-dot"></div>
        </div>
      </div>
    </div>
    
    <div class="debug-content">
      <Spin v-if="loading" size="large" fix>
        <div class="cyber-loader">
          <div class="cyber-loader-inner"></div>
          <div class="cyber-loader-text">{{ langMap.loading[lang] }}</div>
        </div>
      </Spin>
      
      <div v-else class="network-info">
        <div v-for="(network, index) in networkList" :key="index" class="info-card">
          <div class="card-header">
            <div class="card-icon">
              <Icon type="md-wifi" size="32" />
            </div>
            <h2>{{ langMap.networkInfo[lang] }} #{{ index + 1 }}</h2>
            <div class="cyber-corner top-right"></div>
            <div class="cyber-corner bottom-left"></div>
          </div>
          <div class="card-content">
            <div class="network-details">
              <div class="detail-item">
                <div class="detail-label">{{ langMap.networkInterface[lang] }}:</div>
                <div class="detail-value">
                  <span class="value-text">{{ network.ifaName || '-' }}</span>
                  <div class="cyber-underline"></div>
                </div>
              </div>
              
              <div class="detail-item">
                <div class="detail-label">{{ langMap.ipAddress[lang] }}:</div>
                <div class="detail-value">
                  <span class="value-text">{{ network.ipAddress || '-' }}</span>
                  <div class="cyber-underline"></div>
                </div>
              </div>
              
              <div class="detail-item">
                <div class="detail-label">{{ langMap.macAddress[lang] }}:</div>
                <div class="detail-value">
                  <span class="value-text">{{ network.macAddress || '-' }}</span>
                  <div class="cyber-underline"></div>
                </div>
              </div>
            </div>
          </div>
          <div class="card-glow"></div>
        </div>
      </div>
    </div>
    
    <div class="debug-footer">
      <div class="cyber-line"></div>
      <div class="footer-content">
        <div class="system-status">
          <span class="status-dot"></span>
          <span>SYSTEM ONLINE</span>
        </div>
        <div class="timestamp">{{ currentTime }}</div>
      </div>
    </div>
    
    <div class="corner-decoration top-left"></div>
    <div class="corner-decoration top-right"></div>
    <div class="corner-decoration bottom-left"></div>
    <div class="corner-decoration bottom-right"></div>
  </div>
</template>

<script>
import api from "@/api";
import { mapState } from 'vuex';
import langMap from '@/utils/language';

export default {
  name: "NetworkDebug",
  props: {
    robotCode: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      langMap,
      loading: true,
      networkList: [],
      currentTime: ''
    };
  },
  computed: {
    ...mapState({
      lang: state => state.app.language,
    }),
  },
  methods: {
    getNetworkInfo() {
      if (!this.robotCode) {
        this.$Notice.warning({
          title: this.langMap.tip[this.lang],
          desc: '请先选择机器人'
        });
        this.loading = false;
        return;
      }
      
      this.loading = true;
      
      api.getData(`robot-server/robot/get/network/${this.robotCode}`, this).then(res => {
        this.loading = false;
        if (res.state === 0) {
          this.$Notice.error({
            title: this.langMap.tip[this.lang],
            desc: res.msg
          });
        } else {
          try {
            if (res.data && res.data.network) {
              // 解析网络数据，新格式直接是JSON数组字符串
              this.networkList = JSON.parse(res.data.network);
            } else {
              this.networkList = [];
            }
          } catch (error) {
            this.networkList = [];
            this.$Notice.error({
              title: this.langMap.tip[this.lang],
              desc: this.langMap.dataFormatError ? this.langMap.dataFormatError[this.lang] : '数据格式错误'
            });
          }
        }
      }).catch(() => {
        this.loading = false;
      });
    },
    updateTime() {
      const now = new Date();
      const pad = (n) => n.toString().padStart(2, '0');
      this.currentTime = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ` +
        `${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;
    }
  },
  watch: {
    robotCode(newCode) {
      if (newCode) {
        this.getNetworkInfo();
      }
    }
  },
  mounted() {
    if (this.robotCode) {
      this.getNetworkInfo();
    }
    this.updateTime();
    setInterval(this.updateTime, 1000);
  }
};
</script>

<style scoped lang="less">
@keyframes pulse {
  0% { opacity: 0.5; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.2); }
  100% { opacity: 0.5; transform: scale(1); }
}

@keyframes float {
  0% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0px); }
}

@keyframes cyber-glow {
  0% { box-shadow: 0 0 10px 2px rgba(0, 255, 255, 0.3); }
  50% { box-shadow: 0 0 20px 5px rgba(0, 255, 255, 0.5); }
  100% { box-shadow: 0 0 10px 2px rgba(0, 255, 255, 0.3); }
}

@keyframes scan-line {
  0% { transform: translateY(-100%); }
  100% { transform: translateY(100vh); }
}

@keyframes rotate {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.debug-container {
  width: 100%;
  height: ~'calc(100vh - 185px)';
  display: flex;
  flex-direction: column;
  background: #050a14;
  color: #00f0ff;
  font-family: 'Orbitron', 'Roboto', sans-serif;
  position: relative;
  overflow: hidden;
}

.cyber-grid {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: 
    linear-gradient(rgba(0, 240, 255, 0.05) 1px, transparent 1px),
    linear-gradient(90deg, rgba(0, 240, 255, 0.05) 1px, transparent 1px);
  background-size: 40px 40px;
  z-index: 1;
  pointer-events: none;
}

.cyber-glow {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(circle at 50% 50%, rgba(0, 240, 255, 0.1), transparent 70%);
  z-index: 1;
  pointer-events: none;
}

.cyber-glow::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  box-shadow: inset 0 0 100px rgba(0, 240, 255, 0.3);
  z-index: 2;
  animation: pulse-shadow 4s ease-in-out infinite alternate;
}

@keyframes pulse-shadow {
  0% { box-shadow: inset 0 0 80px rgba(0, 240, 255, 0.2); }
  100% { box-shadow: inset 0 0 120px rgba(0, 240, 255, 0.4); }
}

.debug-header {
  padding: 15px 15px;
  background: rgba(0, 10, 20, 0.8);
  border-bottom: 1px solid rgba(0, 240, 255, 0.3);
  position: relative;
  z-index: 2;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.title-container {
  position: relative;
  padding-left: 20px;
}

.cyber-line {
  position: absolute;
  left: 0;
  top: 50%;
  width: 10px;
  height: 70%;
  background: #00f0ff;
  transform: translateY(-50%);
  box-shadow: 0 0 10px rgba(0, 240, 255, 0.8);
}

.debug-title {
  font-size: 28px;
  margin: 0;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 2px;
  color: #fff;
  text-shadow: 0 0 10px rgba(0, 240, 255, 0.8);
}

.robot-code {
  display: flex;
  align-items: center;
  background: rgba(0, 20, 40, 0.8);
  padding: 8px 16px;
  border-radius: 4px;
  border: 1px solid rgba(0, 240, 255, 0.5);
  box-shadow: 0 0 15px rgba(0, 240, 255, 0.3);
}

.code-label {
  color: rgba(0, 240, 255, 0.7);
  margin-right: 8px;
  font-size: 14px;
}

.code-value {
  color: #fff;
  font-weight: 700;
  letter-spacing: 1px;
  font-size: 18px;
}

.pulse-dot {
  width: 10px;
  height: 10px;
  background-color: #00f0ff;
  border-radius: 50%;
  margin-left: 10px;
  animation: pulse 2s infinite ease-in-out;
  box-shadow: 0 0 10px rgba(0, 240, 255, 0.8);
}

.debug-content {
  flex: 1;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 40px 20px;
  position: relative;
  z-index: 2;
  overflow-y: auto;
  max-height: ~"calc(100vh - 310px)"; /* 减去头部和底部的高度 */
}

/* 自定义滚动条样式 */
.debug-content::-webkit-scrollbar {
  width: 8px;
}

.debug-content::-webkit-scrollbar-track {
  background: rgba(0, 10, 20, 0.5);
  border-radius: 4px;
}

.debug-content::-webkit-scrollbar-thumb {
  background: rgba(0, 240, 255, 0.5);
  border-radius: 4px;
  box-shadow: 0 0 5px rgba(0, 240, 255, 0.5);
}

.debug-content::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 240, 255, 0.8);
  box-shadow: 0 0 10px rgba(0, 240, 255, 0.8);
}

.network-info {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 30px;
  width: 100%;
  max-width: 1200px;
  padding: 10px 5px;
}

.info-card {
  background: rgba(0, 20, 40, 0.7);
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 5px 25px rgba(0, 0, 0, 0.5);
  transition: all 0.5s cubic-bezier(0.2, 0.8, 0.2, 1);
  animation: card-float 6s ease-in-out infinite;
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(0, 240, 255, 0.3);
  height: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
  
  &:hover {
    transform: translateY(-15px) scale(1.02);
    border-color: rgba(0, 240, 255, 0.8);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.7), 0 0 30px rgba(0, 240, 255, 0.5);
    z-index: 5;
    
    .card-glow {
      opacity: 1;
    }
    
    .cyber-corner {
      border-width: 3px;
      box-shadow: 0 0 15px rgba(0, 240, 255, 0.8);
    }
  }
}

/* 卡片轻微上下悬浮动画 */
@keyframes card-float {
  0% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0px); }
}

/* 为每个卡片添加不同的动画延迟，使其不同步浮动 */
.info-card:nth-child(2n) {
  animation-delay: -1.5s;
}

.info-card:nth-child(3n) {
  animation-delay: -3s;
}

/* 鼠标悬停效果 */
.info-card:hover {
  transform: translateY(-15px) scale(1.02);
  border-color: rgba(0, 240, 255, 0.8);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.7), 0 0 30px rgba(0, 240, 255, 0.5);
  z-index: 5;
}

/* 卡片四周流动渐变 - 数据流效果 */
.info-card::before {
  content: '';
  position: absolute;
  top: -2px;
  left: -2px;
  right: -2px;
  bottom: -2px;
  background: linear-gradient(45deg, transparent, rgba(36,19,111, 0.6), transparent);
  z-index: -1;
  animation: data-stream 3s linear infinite;
}

@keyframes data-stream {
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
}

/* 卡片内部数据流动光线效果 */
.info-card::after {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, 
    transparent, 
    rgba(0, 240, 255, 0.1), 
    rgba(0, 240, 255, 0.2), 
    rgba(0, 240, 255, 0.1), 
    transparent
  );
  z-index: 1;
  animation: data-glow 4s ease-in-out infinite;
  pointer-events: none;
}

@keyframes data-glow {
  0% { left: -100%; }
  50%, 100% { left: 100%; }
}

/* 顶部边缘发光效果 */
.info-card .card-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(0, 240, 255, 0.8), transparent);
  box-shadow: 0 0 10px rgba(0, 240, 255, 0.5);
}

/* 文本值悬停效果 */
.info-card:hover .value-text {
  transform: translateY(-2px);
  color: #ffffff;
  text-shadow: 0 0 8px rgba(0, 240, 255, 0.8);
  transition: all 0.3s ease;
}

.value-text {
  transition: all 0.3s ease;
}

/* 加强边角特效 */
.info-card:hover .cyber-corner {
  border-width: 3px;
  box-shadow: 0 0 15px rgba(0, 240, 255, 0.8);
}

/* 增强细节线条动画 */
.cyber-underline {
  position: relative;
  overflow: hidden;
}

.cyber-underline::after {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(0, 240, 255, 0.4), transparent);
  animation: line-flow 2s linear infinite;
}

@keyframes line-flow {
  0% { left: -100%; }
  100% { left: 100%; }
}

/* 确保卡片内容在流动光效上方 */
.network-details {
  position: relative;
  z-index: 2;
}

/* 调整内容容器相对位置，让背景效果可以正常显示 */
.card-content {
  position: relative;
}

.card-glow {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  box-shadow: inset 0 0 30px rgba(0, 240, 255, 0.3);
  opacity: 0.5;
  transition: opacity 0.3s ease;
  pointer-events: none;
  z-index: 1;
}

.cyber-corner {
  position: absolute;
  width: 15px;
  height: 15px;
  border: 2px solid rgba(0, 240, 255, 0.5);
  transition: all 0.3s ease;
  
  &.top-right {
    top: -2px;
    right: -2px;
    border-left: none;
    border-bottom: none;
  }
  
  &.bottom-left {
    bottom: -2px;
    left: -2px;
    border-right: none;
    border-top: none;
  }
}

.card-header {
  padding: 20px;
  background: rgba(0, 10, 20, 0.8);
  display: flex;
  align-items: center;
  position: relative;
  border-bottom: 1px solid rgba(0, 240, 255, 0.3);
}

.card-icon {
  width: 60px;
  height: 60px;
  border-radius: 8px;
  background: rgba(0, 20, 40, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 20px;
  border: 1px solid rgba(0, 240, 255, 0.5);
  box-shadow: 0 0 15px rgba(0, 240, 255, 0.3);
  
  i {
    color: #00f0ff;
    text-shadow: 0 0 10px rgba(0, 240, 255, 0.8);
  }
}

.card-header h2 {
  margin: 0;
  font-size: 18px;
  font-weight: 500;
  color: #fff;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.card-content {
  padding: 30px 20px;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 2;
}

.network-details {
  width: 100%;
}

.detail-item {
  margin-bottom: 20px;
  &:last-child {
    margin-bottom: 0;
  }
}

.detail-label {
  color: rgba(0, 240, 255, 0.7);
  font-size: 14px;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.detail-value {
  position: relative;
  margin-left: 10px;
}

.value-text {
  font-size: 16px;
  font-weight: 400;
  color: #fff;
  letter-spacing: 1px;
  word-break: break-all;
}

.cyber-underline {
  height: 2px;
  background: linear-gradient(90deg, transparent, rgba(0, 240, 255, 0.8), transparent);
  margin-top: 5px;
  width: 100%;
  box-shadow: 0 0 10px rgba(0, 240, 255, 0.5);
}

.debug-footer {
  padding: 15px 15px;
  background: rgba(0, 10, 20, 0.8);
  border-top: 1px solid rgba(0, 240, 255, 0.3);
  position: relative;
  z-index: 2;
}

.footer-content {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
  color: rgba(0, 240, 255, 0.7);
}

.system-status {
  display: flex;
  align-items: center;
}

.status-dot {
  width: 8px;
  height: 8px;
  background-color: #00f0ff;
  border-radius: 50%;
  margin-right: 8px;
  animation: pulse 2s infinite ease-in-out;
  box-shadow: 0 0 10px rgba(0, 240, 255, 0.8);
}

.timestamp {
  font-family: 'Courier New', monospace;
  letter-spacing: 1px;
}

.corner-decoration {
  position: absolute;
  width: 51px;
  height: 51px;
  pointer-events: none;
  z-index: 3;
  
  &.top-left {
    top: 0;
    left: 0;
    border-top: 2px solid rgba(0, 240, 255, 0.5);
    border-left: 2px solid rgba(0, 240, 255, 0.5);
    box-shadow: -5px -5px 15px rgba(0, 240, 255, 0.3);
    height:75px
  }
  
  &.top-right {
    top: 0;
    right: 0;
    border-top: 2px solid rgba(0, 240, 255, 0.5);
    border-right: 2px solid rgba(0, 240, 255, 0.5);
    box-shadow: 5px -5px 15px rgba(0, 240, 255, 0.3);
    height:75px
  }
  
  &.bottom-left {
    bottom: 0;
    left: 0;
    border-bottom: 2px solid rgba(0, 240, 255, 0.5);
    border-left: 2px solid rgba(0, 240, 255, 0.5);
    box-shadow: -5px 5px 15px rgba(0, 240, 255, 0.3);
  }
  
  &.bottom-right {
    bottom: 0;
    right: 0;
    border-bottom: 2px solid rgba(0, 240, 255, 0.5);
    border-right: 2px solid rgba(0, 240, 255, 0.5);
    box-shadow: 5px 5px 15px rgba(0, 240, 255, 0.3);
  }
}

.cyber-loader {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.cyber-loader-inner {
  width: 80px;
  height: 80px;
  border: 3px solid transparent;
  border-top: 3px solid rgba(0, 240, 255, 0.8);
  border-right: 3px solid rgba(0, 240, 255, 0.6);
  border-radius: 50%;
  animation: rotate 1s linear infinite;
  box-shadow: 0 0 20px rgba(0, 240, 255, 0.5);
  margin-bottom: 15px;
  position: relative;
  
  &:before {
    content: '';
    position: absolute;
    top: 10px;
    left: 10px;
    right: 10px;
    bottom: 10px;
    border: 3px solid transparent;
    border-bottom: 3px solid rgba(0, 240, 255, 0.8);
    border-left: 3px solid rgba(0, 240, 255, 0.6);
    border-radius: 50%;
    animation: rotate 1.5s linear infinite reverse;
  }
}

.cyber-loader-text {
  color: #00f0ff;
  text-transform: uppercase;
  letter-spacing: 2px;
  font-size: 16px;
  text-shadow: 0 0 10px rgba(0, 240, 255, 0.8);
}

/* 粒子动画相关样式 */
.particle-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  z-index: 1;
  pointer-events: none;
}

.particle {
  position: absolute;
  background: radial-gradient(circle at center, rgba(0, 240, 255, 0.8) 0%, rgba(0, 240, 255, 0.3) 50%, transparent 70%);
  pointer-events: none;
}

.p1, .p5, .p9 {
  width: 100px;
  height: 100px;
  filter: blur(10px);
}

.p2, .p6, .p10 {
  width: 80px;
  height: 80px;
  filter: blur(8px);
}

.p3, .p7, .p11 {
  width: 60px;
  height: 60px;
  filter: blur(6px);
}

.p4, .p8, .p12 {
  width: 40px;
  height: 40px;
  filter: blur(4px);
}

@keyframes float-particle-1 {
  0% { transform: translate(0, 100vh) scale(0.5); opacity: 0; }
  20% { opacity: 0.2; }
  50% { transform: translate(20px, 0) scale(1); opacity: 0.5; }
  80% { opacity: 0.2; }
  100% { transform: translate(40px, -100vh) scale(0.5); opacity: 0; }
}

@keyframes float-particle-2 {
  0% { transform: translate(0, 100vh) scale(0.5); opacity: 0; }
  20% { opacity: 0.2; }
  50% { transform: translate(-30px, 0) scale(1); opacity: 0.5; }
  80% { opacity: 0.2; }
  100% { transform: translate(-60px, -100vh) scale(0.5); opacity: 0; }
}

@keyframes float-particle-3 {
  0% { transform: translate(0, 100vh) scale(0.5); opacity: 0; }
  20% { opacity: 0.2; }
  50% { transform: translate(15px, 0) scale(1); opacity: 0.5; }
  80% { opacity: 0.2; }
  100% { transform: translate(30px, -100vh) scale(0.5); opacity: 0; }
}

@keyframes float-particle-4 {
  0% { transform: translate(0, 100vh) scale(0.5); opacity: 0; }
  20% { opacity: 0.2; }
  50% { transform: translate(-25px, 0) scale(1); opacity: 0.5; }
  80% { opacity: 0.2; }
  100% { transform: translate(-50px, -100vh) scale(0.5); opacity: 0; }
}

@keyframes float-particle-5 {
  0% { transform: translate(0, 100vh) scale(0.5); opacity: 0; }
  20% { opacity: 0.2; }
  50% { transform: translate(35px, 0) scale(1); opacity: 0.5; }
  80% { opacity: 0.2; }
  100% { transform: translate(70px, -100vh) scale(0.5); opacity: 0; }
}

@keyframes float-particle-6 {
  0% { transform: translate(0, 100vh) scale(0.5); opacity: 0; }
  20% { opacity: 0.2; }
  50% { transform: translate(-15px, 0) scale(1); opacity: 0.5; }
  80% { opacity: 0.2; }
  100% { transform: translate(-30px, -100vh) scale(0.5); opacity: 0; }
}

.p1 { animation: float-particle-1 20s infinite; }
.p2 { animation: float-particle-2 25s infinite; animation-delay: -5s; }
.p3 { animation: float-particle-3 22s infinite; animation-delay: -2s; }
.p4 { animation: float-particle-4 18s infinite; animation-delay: -7s; }
.p5 { animation: float-particle-5 24s infinite; animation-delay: -3s; }
.p6 { animation: float-particle-6 21s infinite; animation-delay: -9s; }
.p7 { animation: float-particle-1 26s infinite; animation-delay: -4s; }
.p8 { animation: float-particle-2 19s infinite; animation-delay: -8s; }
.p9 { animation: float-particle-3 23s infinite; animation-delay: -6s; }
.p10 { animation: float-particle-4 17s infinite; animation-delay: -10s; }
.p11 { animation: float-particle-5 27s infinite; animation-delay: -11s; }
.p12 { animation: float-particle-6 16s infinite; animation-delay: -1s; }

@media (max-width: 768px) {
  .network-info {
    grid-template-columns: 1fr;
  }
  
  .header-content {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .robot-code {
    margin-top: 15px;
  }
  
  .debug-header {
    padding: 20px;
  }
  
  .debug-footer {
    padding: 15px 20px;
  }
  
  .footer-content {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .timestamp {
    margin-top: 10px;
  }
}
</style>
